import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { getAddress, type Address, type WalletClient } from 'viem';

import { WalletService } from '../services/wallet';
import { CUSTOS_ENVIRONMENT, type CustosEnvironment } from '../models/environment';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storage';
import type { LoginInfo, LoginRequestPayload, LoginResponse } from './auth.types';

/**
 * Signature-based wallet login against Custos `/api/dlogin/login.do`
 * (proxied through our Cloudflare Worker for CORS). Auto-runs whenever a
 * wallet address appears and no valid session is held. Persists the session
 * in localStorage keyed by wallet address so page reloads don't re-trigger
 * the wallet signing prompt.
 *
 * Invite flow:
 *   - The URL `?invite=<f_user_info>` is captured on app start.
 *   - The invite survives page reloads via localStorage.
 *   - If an invite is already stored, a new URL param does NOT overwrite it
 *     (protects an existing inviter attribution from being hijacked).
 *   - On first login, we send the stored invite as `inviteuser`; the backend
 *     commits the binding permanently. After successful login, if the
 *     backend's returned `f_invite_user` is non-empty (binding accepted),
 *     we clear the localStorage invite — the relationship now lives on the
 *     backend and doesn't need to be re-sent.
 */
const SESSION_KEY_PREFIX = 'custos.auth.session';
const INVITE_KEY = 'custos.invite';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly wallet = inject(WalletService);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);
  private readonly platformId = inject(PLATFORM_ID);

  // ── Public state ──────────────────────────────────────────────
  private readonly _loginInfo = signal<LoginInfo | null>(null);
  readonly loginInfo = this._loginInfo.asReadonly();

  /** True iff a session is present AND its `tokenendtime` hasn't passed. */
  readonly isLoggedIn = computed(() => {
    const info = this._loginInfo();
    if (!info) return false;
    return Date.now() / 1000 < info.tokenendtime;
  });

  /** Token to attach to authed API calls, or null when not logged in. */
  readonly token = computed(() => (this.isLoggedIn() ? this._loginInfo()!.token : null));

  /**
   * Origin captured at construction time so `inviteUrl` stays a pure computed
   * (no side-effectful `window` reads inside the computation). Null on SSR.
   */
  private readonly origin: string | null = isPlatformBrowser(this.platformId)
    ? window.location.origin
    : null;

  /** Shareable invite link — null until login completes. */
  readonly inviteUrl = computed(() => {
    if (!this.origin) return null;
    const info = this._loginInfo();
    if (!info) return null;
    return `${this.origin}/?invite=${info.f_user_info}`;
  });

  private readonly _isLoggingIn = signal(false);
  readonly isLoggingIn = this._isLoggingIn.asReadonly();

  private readonly _error = signal<string | null>(null);
  readonly error = this._error.asReadonly();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.captureInviteFromUrl();
      this.restoreSessionForCurrentAddress();
    }

    // Restore / clear in-memory session on wallet address changes. NO auto
    // signing — signing is only triggered by an explicit `login()` call
    // from the Connect flow. This avoids spurious prompts on reconnect and
    // keeps the UX contract simple: you click Connect, you get exactly one
    // signature prompt.
    effect(() => {
      const addr = this.wallet.address();
      if (!addr) {
        this._loginInfo.set(null);
        return;
      }
      const cached = this.readSession(addr);
      if (cached && this.isSessionValid(cached)) {
        this._loginInfo.set(cached);
      } else {
        // Previously logged-in as a different address — clear stale in-memory
        // state but don't auto-prompt. The Connect caller will invoke
        // `ensureLoggedIn()` if they need a fresh session.
        this._loginInfo.set(null);
      }
    });
  }

  /**
   * Run the full sign + POST flow. Idempotent: a second call while one is
   * in-flight is a no-op. The wallet's `signMessage` prompt is the only
   * user-visible side effect; everything else is either local state or a
   * single Worker request.
   */
  async login(): Promise<void> {
    if (this._isLoggingIn()) return;
    const addr = this.wallet.address();
    if (!addr) {
      this._error.set('Wallet not connected');
      return;
    }

    this._isLoggingIn.set(true);
    this._error.set(null);
    try {
      // Wait until WalletService has finished wiring up its walletClient.
      // `tryReconnect` sets the address signal BEFORE instantiating the
      // client, so the effect that calls us can land in a window where
      // `getWalletClient()` would throw. We poll briefly rather than race.
      const client = await this.waitForWalletClient();
      // Normalise to EIP-55 checksum — the upstream's existing sample payload
      // uses checksum casing, and a strict string compare on the server would
      // otherwise reject a lowercase-from-MetaMask address (`code:"99"`).
      const checksumAddr = getAddress(addr) as Address;
      const time = Math.floor(Date.now() / 1000).toString();
      const nonce = this.generateNonce();
      const message = this.buildMessage(checksumAddr, nonce);
      const sign = (await client.signMessage({ account: checksumAddr, message })) as `0x${string}`;

      const inviteuser = this.getPendingInvite();
      const payload: LoginRequestPayload = {
        sign,
        useraddr: checksumAddr,
        time,
        data: message,
        inviteuser,
      };

      // Call the upstream Custos login endpoint directly with the payload as
      // a URL-encoded form field named `p`. The upstream is a legacy Java-
      // style endpoint: it accepts either `application/x-www-form-urlencoded`
      // or the same `p=` as a query string, but NOT `multipart/form-data`
      // (which returns {code:"2",msg:"JSON format error"}).
      //
      // 15s AbortController timeout so a hung upstream doesn't leave
      // isLoggingIn stuck forever (would freeze the Connect button).
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15_000);
      let response: LoginResponse;
      try {
        const body = new URLSearchParams({ p: JSON.stringify(payload) });
        const httpResp = await fetch(`${this.env.authBaseUrl}/api/dlogin/login.do`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
          signal: ctrl.signal,
        });
        response = (await httpResp.json()) as LoginResponse;
      } finally {
        clearTimeout(timer);
      }

      if (response.code !== '0' || !this.isValidLoginInfo(response.info)) {
        throw new Error(response.msg || 'Login failed');
      }

      this._loginInfo.set(response.info);
      this.writeSession(addr, response.info);

      // Invite has been committed on the backend if it accepted a binding —
      // clear our pending copy so we don't resend on future logins.
      if (response.info.f_invite_user && response.info.f_invite_user !== '') {
        safeRemoveItem(INVITE_KEY);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      this._error.set(msg);
      console.error('[AuthService] login failed:', err);
    } finally {
      this._isLoggingIn.set(false);
    }
  }

  /**
   * Run login only if we don't already have a valid session. Designed for the
   * Connect Wallet flow: after the wallet connects, the caller invokes this
   * to either confirm the cached session is still good or prompt for a fresh
   * signature — exactly once. Idempotent.
   */
  async ensureLoggedIn(): Promise<void> {
    if (this.isLoggedIn()) return;
    await this.login();
  }

  /** Clear the in-memory session and remove the stored session for this address. */
  logout(): void {
    const addr = this.wallet.address();
    if (addr) this.clearSession(addr);
    this._loginInfo.set(null);
  }

  /**
   * Up to ~2 seconds of 50ms polls for the wallet client. `WalletService`
   * sets its address signal before the walletClient exists during reconnect,
   * so a naive `getWalletClient()` call right when the effect fires can throw.
   */
  private async waitForWalletClient(): Promise<WalletClient> {
    const MAX_ATTEMPTS = 40;
    const INTERVAL_MS = 50;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        return this.wallet.getWalletClient();
      } catch {
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }
    }
    throw new Error('Wallet client not ready');
  }

  // ── Internal helpers ──────────────────────────────────────────

  /**
   * Message format copied verbatim from the existing Custos production flow.
   * DO NOT reformat — whitespace is part of the signed payload.
   */
  private buildMessage(addr: `0x${string}`, nonce: string): string {
    return [
      'Welcome to Custos!',
      '',
      'Click to sign in for the purpose of verifying your identity and protecting your personal information.',
      '',
      'No blockchain transactions or gas fees are required.',
      '',
      'Your authentication status will expire after 24 hours.',
      '',
      `Wallet address: ${addr}`,
      '',
      `Nonce: ${nonce}`,
    ].join('\n');
  }

  /**
   * Produce a nonce that matches the existing backend's expected shape
   * (6 uppercase letters, hyphen, 7 uppercase hex chars). Uses rejection
   * sampling on crypto-random bytes so the letter distribution is uniform
   * (naive `byte % 26` biases A-F — 256 % 26 = 22 leftover slots).
   */
  private generateNonce(): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const hex = '0123456789ABCDEF';
    let prefix = '';
    for (let i = 0; i < 6; i++) prefix += this.randomChar(letters);
    let suffix = '';
    for (let i = 0; i < 7; i++) suffix += this.randomChar(hex);
    return `${prefix}-${suffix}`;
  }

  /** Uniform-random character pick from an alphabet via rejection sampling. */
  private randomChar(alphabet: string): string {
    const n = alphabet.length;
    const limit = 256 - (256 % n);
    const buf = new Uint8Array(1);
    while (true) {
      if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(buf);
      } else {
        buf[0] = Math.floor(Math.random() * 256);
      }
      if (buf[0] < limit) return alphabet[buf[0] % n];
    }
  }

  /**
   * Minimal runtime check on the upstream response. Guards against upstream
   * format drift (e.g. `{code:"0",info:null}` or a success envelope missing
   * `tokenendtime`) which would otherwise leave us with NaN-based session
   * validity and a bad invite URL.
   */
  private isValidLoginInfo(info: LoginInfo | null | undefined): info is LoginInfo {
    if (!info) return false;
    return (
      typeof info.token === 'string' &&
      info.token.length > 0 &&
      typeof info.tokenendtime === 'number' &&
      Number.isFinite(info.tokenendtime) &&
      typeof info.f_user_info === 'string' &&
      info.f_user_info.length > 0
    );
  }

  // ── Invite handling ───────────────────────────────────────────

  private captureInviteFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (!invite) return;
    const existing = safeGetItem(INVITE_KEY);
    // Don't overwrite an existing binding. First-seen invite wins.
    if (existing) return;
    safeSetItem(INVITE_KEY, invite);
  }

  /**
   * Returns the invite we should send on the NEXT login, priority:
   *   1. An existing binding from the backend (already tied to this wallet)
   *   2. The localStorage invite captured from `?invite=`
   *   3. "" (no invite)
   */
  private getPendingInvite(): string {
    const info = this._loginInfo();
    if (info?.f_invite_user) return info.f_invite_user;
    return safeGetItem(INVITE_KEY) ?? '';
  }

  // ── Session persistence (per-address so switching wallets is clean) ──

  private sessionKey(addr: string): string {
    return `${SESSION_KEY_PREFIX}:${addr.toLowerCase()}`;
  }

  private readSession(addr: string): LoginInfo | null {
    const raw = safeGetItem(this.sessionKey(addr));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LoginInfo;
    } catch {
      return null;
    }
  }

  private writeSession(addr: string, info: LoginInfo): void {
    safeSetItem(this.sessionKey(addr), JSON.stringify(info));
  }

  private clearSession(addr: string): void {
    safeRemoveItem(this.sessionKey(addr));
  }

  private isSessionValid(info: LoginInfo): boolean {
    return Date.now() / 1000 < info.tokenendtime;
  }

  private restoreSessionForCurrentAddress(): void {
    const addr = this.wallet.address();
    if (!addr) return;
    const cached = this.readSession(addr);
    if (cached && this.isSessionValid(cached)) this._loginInfo.set(cached);
  }
}
